import { useEffect, useRef } from "react";
import { format } from "date-fns";

const ALIGN_KEY = "eps_jobsheet_align";

function getAlignData() {
  try { return JSON.parse(localStorage.getItem(ALIGN_KEY) || "{}"); }
  catch (_) { return {}; }
}

const mm = (v) => `${(v * 0.254).toFixed(2)}mm`;

const PrintJobsheet = ({ job, onDone }) => {
  const hasPrinted = useRef(false);

  useEffect(() => {
    if (hasPrinted.current) return;
    hasPrinted.current = true;

    const d = job.job_date ? new Date(job.job_date) : null;
    const dateStr = d && !isNaN(d) ? format(d, "d MMMM yyyy") : (job.job_date || "");

    const align = getAlignData();
    const gx = (align["_global"]?.dx || 0) / 0.254;
    const gy = (align["_global"]?.dy || 0) / 0.254;

    const x = (vbX, key = "") => vbX + gx + ((align[key]?.dx || 0) / 0.254);
    const y = (vbY, key = "") => vbY + gy + ((align[key]?.dy || 0) / 0.254);

    // Ellipse only in roll mode (Face In / Face Out); no ellipse for sheet
    const flwVal = (job.flw || "").trim();
    let ellipseVbX = null, ellipseKey = null;
    if (flwVal === "Face Out") {
      ellipseVbX = 545; ellipseKey = "ellipse_fo";
    } else if (flwVal === "Face In") {
      ellipseVbX = 585; ellipseKey = "ellipse_fi";
    }

    // Perforation X mark — PEFORATED → left (pefo_yes), anything else → right (pefo_no)
    const pefoVal = (job.pefo || "").trim();
    const showPefoX = pefoVal.length > 0;
    const isPeforated = pefoVal.toUpperCase() === "PEFORATED";
    const pefoVbX  = isPeforated ? 450 : 610;
    const pefoKey  = isPeforated ? "pefo_yes" : "pefo_no";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Jobsheet #${job.job_id}</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 210mm; height: 297mm; overflow: hidden; }
    .page {
      position: relative;
      width: 210mm;
      height: 297mm;
      font-family: "AvantGarde Bk BT", "Century Gothic", Arial, sans-serif;
      color: #000;
    }
    .f { position: absolute; font-size: 14pt; white-space: nowrap; line-height: 1; }
    .f-lg { font-size: 20pt; }
    .f-sm { font-size: 10pt; }
    .ellipse { position: absolute; border: 2px solid #000; border-radius: 50%; }
    .note-block { position: absolute; font-size: 14pt; white-space: pre-wrap; word-wrap: break-word; }
  </style>
</head>
<body>
<div class="page">

  <!-- Job ID -->
  <div class="f f-lg" style="left:${mm(x(380,'job_id'))};top:${mm(y(100,'job_id'))};">${job.job_id}</div>

  <!-- Left column -->
  <div class="f" style="left:${mm(x(145,'job_date'))};top:${mm(y(185,'job_date'))};">${dateStr}</div>
  <div class="f" style="left:${mm(x(145,'nme'))};top:${mm(y(222,'nme'))};">${job.nme || ""}</div>
  <div class="f" style="left:${mm(x(145,'job_name'))};top:${mm(y(258,'job_name'))};">${job.job_name || ""}</div>
  <div class="f" style="left:${mm(x(145,'mat_name'))};top:${mm(y(295,'mat_name'))};">${job.mat_name || ""}</div>
  <div class="f" style="left:${mm(x(145,'mat_clr'))};top:${mm(y(329,'mat_clr'))};">${job.mat_clr || ""}</div>
  <div class="f" style="left:${mm(x(145,'size'))};top:${mm(y(368,'size'))};">${job.size || ""}</div>
  <div class="f" style="left:${mm(x(145,'ptg'))};top:${mm(y(408,'ptg'))};">${job.ptg || ""}</div>
  <div class="f" style="left:${mm(x(145,'pn_cust'))};top:${mm(y(444,'pn_cust'))};">${job.pn_cust || ""}</div>
  <div class="f" style="left:${mm(x(145,'mpn_elk'))};top:${mm(y(481,'mpn_elk'))};">${job.mpn_elk || ""}</div>
  <div class="f" style="left:${mm(x(145,'drw_no'))};top:${mm(y(515,'drw_no'))};">${job.drw_no || ""}</div>
  <div class="f" style="left:${mm(x(145,'die_no'))};top:${mm(y(554,'die_no'))};">${job.die_no || ""}</div>

  <!-- Prepare By -->
  <div class="f f-sm" style="left:${mm(x(43,'admin'))};top:${mm(y(730,'admin'))};">${job.admin || ""}</div>

  <!-- Right column: Roll / Core -->
  <div class="f" style="left:${mm(x(562,'roll'))};top:${mm(y(293,'roll'))};">${job.roll || ""}</div>
  <div class="f" style="left:${mm(x(562,'core'))};top:${mm(y(334,'core'))};">${job.core || ""}</div>

  <!-- Ellipse: roll mode only (Face In=585, Face Out=545) -->
  ${ellipseKey ? `<div class="ellipse" style="left:${mm(x(ellipseVbX, ellipseKey))};top:${mm(y(366, ellipseKey))};width:${mm(81)};height:${mm(45)};"></div>` : ""}

  <!-- Sheet value at flw position -->
  <div class="f" style="left:${mm(x(542,'sht_val'))};top:${mm(y(437,'sht_val'))};">${job.sht || ""}</div>

  <!-- Comp. Label (above Std Packing) -->
  <div class="f" style="left:${mm(x(622,'cmp_label'))};top:${mm(y(474,'cmp_label'))};">${job.cmp_label || ""}</div>

  <!-- Standard Packing -->
  <div class="f" style="left:${mm(x(636,'std_pack'))};top:${mm(y(518,'std_pack'))};">${job.std_pack || ""}</div>

  <!-- Peforated X mark -->
  ${showPefoX ? `<div class="f" style="left:${mm(x(pefoVbX, pefoKey))};top:${mm(y(560, pefoKey))};">X</div>` : ""}

  <!-- Requirement & Lamination -->
  <div class="f" style="left:${mm(x(600,'required'))};top:${mm(y(632,'required'))};">${job.required || ""}</div>
  <div class="f" style="left:${mm(x(600,'lami'))};top:${mm(y(662,'lami'))};">${job.lami || ""}</div>

  <!-- Notes -->
  <div class="note-block" style="left:${mm(x(22,'note'))};top:${mm(y(875,'note'))};width:${mm(230)};height:${mm(183)};">${job.note || ""}</div>

</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) { onDone?.(); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
      onDone?.();
    }, 500);
  }, []);

  return null;
};

export default PrintJobsheet;
